import { ChatReplyTemplateMenu } from '@/features/ChatReplyTemplates/ChatReplyTemplateMenu';
import { isBadRequestError } from '@/core/api/badRequestError';
import { useRef, useState, type KeyboardEvent } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { chatDialogFormToSendRequest } from '@/entities/ChatDialog/mappers';
import { chatDialogSendMessageSchema, CHAT_DIALOG_MESSAGE_MAX_LENGTH, CHAT_PHOTO_CAPTION_MAX_LENGTH } from '@/entities/ChatDialog/model/sendMessageSchema';
import { useChatDialogWorkspace } from '@/entities/ChatDialog/model/workspace';
import type { ChatDialogSendMessageFormValues } from '@/entities/ChatDialog/model/types';
import { ChatPhotoAttachments } from './ChatPhotoAttachments';
import { getErrorMessage } from '@/shared/lib/errors';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { RequestError } from '@/shared/ui/RequestState';
import type { ChatDialogComposerProps, ChatDialogSendAttempt } from './types';
import classes from './ChatDialogComposer.module.scss';

const { TextareaField } = createRHFFields<ChatDialogSendMessageFormValues>('chat-message');

/** Показывает форму ответа пользователю */
export const ChatDialogComposer = (
    { dialogId, disabled, templateMenuRef }: ChatDialogComposerProps
) => {
    const sendMessage = useChatDialogWorkspace((state) => state.sendMessage);
    const attempt = useRef<ChatDialogSendAttempt | null>(null);
    const [showPhoto, setShowPhoto] = useState(false);
    const [uploading, setUploading] = useState(false);

    const form = useForm<ChatDialogSendMessageFormValues>({
        resolver: zodResolver(chatDialogSendMessageSchema),
        defaultValues: {
            text: '',
            photoUrls: []
        },
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
    });

    const isSubmitting = form.formState.isSubmitting;
    const photoUrls = form.watch('photoUrls') ?? [];
    const hasContent = Boolean(form.watch('text').trim()) || photoUrls.length > 0;
    const busy = disabled || isSubmitting || uploading;

    /** Отправляет ответ пользователю */
    const onSubmit = async (values: ChatDialogSendMessageFormValues): Promise<void> => {
        if (disabled || uploading) return;
        form.clearErrors('root.server');

        const sameAttempt = attempt.current?.text === values.text
            && JSON.stringify(attempt.current?.photoUrls ?? []) === JSON.stringify(values.photoUrls ?? []);

        if (!sameAttempt || !attempt.current) {
            attempt.current = {
                clientMessageId: crypto.randomUUID(),
                text: values.text,
                photoUrls: values.photoUrls,
            };
        }

        try {
            await sendMessage(dialogId, chatDialogFormToSendRequest(values, attempt.current.clientMessageId));
            attempt.current = null;

            form.reset({
                text: '',
                photoUrls: []
            });

            setShowPhoto(false);
        } catch (error: unknown) {
            if (isBadRequestError(error)) return;
            form.setError('root.server', { message: getErrorMessage(error, 'Не удалось сохранить ответ') });
        }
    };

    /** Отправляет ответ по Enter и переносит строку по Shift+Enter или Alt+Enter */
    const onKeyDown = (event: KeyboardEvent<HTMLFormElement>): void => {
        const ignore = event.key !== 'Enter' || !(event.target instanceof HTMLTextAreaElement)
            || event.nativeEvent.isComposing || event.keyCode === 229;

        if (ignore || !(event.target instanceof HTMLTextAreaElement)) return;
        if (event.altKey || event.shiftKey) {
            event.preventDefault();
            const textarea = event.target;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;

            form.setValue('text', `${textarea.value.slice(0, start)}\n${textarea.value.slice(end)}`, {
                shouldDirty: true,
            });

            requestAnimationFrame(() => textarea.setSelectionRange(start + 1, start + 1));

            return;
        }
        event.preventDefault();

        if (!event.repeat && !busy && hasContent) void form.handleSubmit(onSubmit)();
    };

    return (
        <FormProvider {...form}>
            <form
                className={classes.form}
                aria-label="Ответ пользователю"
                onSubmit={(event) => {
                    event.preventDefault();

                    if (!busy && hasContent) void form.handleSubmit(onSubmit)(event);
                }}
                onKeyDown={onKeyDown}
                noValidate
            >
                {(showPhoto || photoUrls.length > 0) && (
                    <div className={classes.photoPanel}>
                        <Controller
                            name="photoUrls"
                            control={form.control}
                            render={({ field }) => (
                                <ChatPhotoAttachments
                                    value={field.value ?? []}
                                    disabled={disabled || isSubmitting}
                                    onChange={(urls) => {
                                        field.onChange(urls);

                                        if (urls.length > 0 || form.getValues('text').trim()) {
                                            void form.trigger('text');
                                        } else {
                                            form.clearErrors('text');
                                        }
                                    }}
                                    onBusyChange={setUploading}
                                />
                            )}
                        />
                    </div>
                )}
                <div className={classes.editorRow}>
                    <ActionButton
                        type="button"
                        className={classes.attachButton}
                        icon="pi pi-paperclip"
                        iconOnly
                        color="secondary"
                        aria-label="Прикрепить фотографии"
                        aria-expanded={showPhoto || photoUrls.length > 0}
                        title="Прикрепить фотографии"
                        disabled={busy}
                        onClick={() => setShowPhoto((value) => !value)}
                    />
                    <div className={classes.editor}>
                        <ChatReplyTemplateMenu
                            triggerRef={templateMenuRef}
                            disabled={busy}
                            maxLength={photoUrls.length > 0
                                ? CHAT_PHOTO_CAPTION_MAX_LENGTH
                                : CHAT_DIALOG_MESSAGE_MAX_LENGTH
                            }
                            onInsert={text => form.setValue('text', text, {
                                shouldDirty: true,
                                shouldValidate: true,
                            })}
                        >
                            <TextareaField
                                name="text"
                                label="Сообщение"
                                placeholder={photoUrls.length > 0
                                    ? "Добавьте подпись к фотографиям"
                                    : "Напишите ответ"
                                }
                                rows={1}
                                autoResize
                                maxLength={photoUrls.length > 0
                                    ? CHAT_PHOTO_CAPTION_MAX_LENGTH
                                    : CHAT_DIALOG_MESSAGE_MAX_LENGTH
                                }
                                disabled={busy}
                            />
                        </ChatReplyTemplateMenu>
                    </div>
                    <div className={classes.actions}>
                        <ActionButton
                            type="submit"
                            className={classes.sendButton}
                            aria-label="Отправить"
                            title="Отправить"
                            iconOnly
                            icon="pi pi-arrow-up"
                            loading={isSubmitting}
                            disabled={busy || !hasContent}
                        />
                    </div>
                </div>
                {form.formState.errors.root?.server?.message && (
                    <RequestError
                        message={form.formState.errors.root.server.message}
                        compact
                    />
                )}
            </form>
        </FormProvider>
    );
};
